/*
 * coveo-cart-sync.js -- keep the Coveo commerce cart in sync with the Shopify cart.
 *
 * Two jobs:
 *   1. SEED -- on load, push the current cart (data-cart attribute JSON) into the Coveo cart.
 *      Idempotent backstop only: the engine config is already built with the load-time cart
 *      (coveo-commerce-init.liquid). It cannot be the primary seed because this element can
 *      attach only after a surface has set window.__coveoEngine, which is after that surface
 *      has fired its first slot query.
 *   2. LIVE SYNC -- subscribe to the theme's AJAX cart-update event so the Coveo cart re-syncs on
 *      every change, not just at load. Shopify has no native cart-changed DOM event, so the
 *      listener is guarded: harmless until the theme broadcasts the event; seed-at-load remains
 *      the load-time backstop.
 *
 * The buildCart(engine) controller has NO setItems method (only updateItemQuantity / empty /
 * purchase / state / subscribe). Whole-cart replace is the redux ACTION
 * loadCartActions(engine).setItems, dispatched engine.dispatch(setItems(items)). Prefer the
 * whole-cart replace over per-item updateItemQuantity so the controller and the app's web pixel
 * don't double-count ec.cartAction (verify in Coveo Explorer).
 *
 * Runtime handoff (no Liquid in a theme asset):
 *   - cart JSON: the host element's data-cart attribute (set by the rendering snippet).
 *   - engine: window.__coveoEngine, set by coveo-commerce-init.liquid's coveoConfigureEngine().
 *     Resolved by a bounded poll, then by the coveo:engine-ready event so an engine that
 *     publishes after the poll window (slow network / delayed consent) still initialises the
 *     cart actions instead of the element staying dead until reload.
 *
 * Sync signal: a completed cart-change cycle dispatches coveo-cart:synced ONLY when setItems
 * actually reached the engine (accurate telemetry, and cart-context surfaces re-query against a
 * cart that truly changed); otherwise it dispatches coveo-cart:skipped with a reason, so an
 * observer is never left waiting on a synced that will never come. Both carry detail.itemCount.
 * An engine that publishes after the cart changed is seeded with that live cart and announced
 * with a synced, so a surface booted by the change queries the current cart.
 *
 * Cart item shape (verified, @coveo/headless 3.52 CartItemWithMetadata):
 *   { productId: 'gid://shopify/ProductVariant/' + variant_id, name, price, quantity }
 * productId is the ProductVariant global id -- NOT the product id, NOT the bare variant id.
 * price is major currency units: Shopify final_price is minor units (cents), Coveo Commerce
 * prices are major units, so the mapping divides by 100 to match the catalog and web pixel.
 */

const COVEO_HEADLESS_CDN = "https://static.cloud.coveo.com/shopify/v1/headless/commerce.esm.js";

// Conventional pub/sub channel the theme broadcasts after /cart/{add,change,update,clear}.js.
const CART_UPDATE_EVENT = "cart:updated";

// Fired on `document` once a cart-change sync cycle has finished pushing the latest cart into
// the shared engine. Cart-context surfaces (coveo-recommendations) refresh on THIS instead of
// guessing a timer, so their re-query always runs against the current cart, never the pre-change
// one (the setItems below completes only after the async /cart.js fetch resolves).
const CART_SYNCED_EVENT = "coveo-cart:synced";

// Fired on `document` in place of CART_SYNCED_EVENT when a cart-change cycle did NOT push the
// cart into the engine -- no engine on this page, or the sync threw. `detail.reason` is
// 'no-engine' | 'error'. Keeps the synced signal honest without leaving an observer hanging.
const CART_SYNC_SKIPPED_EVENT = "coveo-cart:skipped";

// The engine can publish after resolveEngine's poll window expires; coveo-commerce-init
// dispatches this the moment it sets window.__coveoEngine, so a late engine still wires up.
const ENGINE_READY_EVENT = "coveo:engine-ready";

// Bounded poll for a not-yet-published engine before we fall back to the ready event.
const ENGINE_POLL_STEP_MS = 100;
const ENGINE_POLL_LIMIT_MS = 8000;

function mapShopifyLineToCoveoItem(line) {
  return {
    productId: "gid://shopify/ProductVariant/" + line.variant_id,
    name: line.product_title || line.title,
    // Shopify final_price is minor units (cents); Coveo Commerce expects major units.
    price: line.final_price / 100,
    quantity: line.quantity,
  };
}

// Both signals carry the cart's item count. With a full cart in the event detail they fire
// synchronously inside the same cart:updated dispatch, so a listener registered after this one
// has not seen the new cart yet and must read the count from here. null when unknown.
function itemCountOf(cart) {
  return cart && typeof cart.item_count === "number" ? cart.item_count : null;
}

class CoveoCartSync extends HTMLElement {
  constructor() {
    super();
    this.engine = null;
    this.cartActions = null;
    // The newest full cart a cart:updated handed us. Seeds a late engine instead of the stale
    // load-time data-cart, so a first add made before any surface built the engine still reaches it.
    this.latestCart = null;
    this.syncFromCartJson = this.syncFromCartJson.bind(this);
    this.onEngineReady = this.onEngineReady.bind(this);
  }

  async connectedCallback() {
    // Live sync is harmless without an engine (replaceItems no-ops), so attach it up front and
    // keep it regardless of how the engine resolves.
    this.attachLiveSync();
    // The interface engine and the headless cart actions are needed before we can dispatch.
    // Resolve the SAME engine the surface initialised with so the cart controller and the
    // interface share one state tree.
    try {
      const engine = await this.resolveEngine();
      if (engine) {
        await this.initWithEngine(engine);
        return;
      }
      // Poll window expired with no engine. It can still publish later on a slow network or
      // after delayed consent/engine init -- re-check once for the sub-tick gap, then listen
      // for the ready signal so cart actions initialise past the polling window.
      if (window.__coveoEngine) {
        await this.initWithEngine(window.__coveoEngine);
      } else {
        document.addEventListener(ENGINE_READY_EVENT, this.onEngineReady, { once: true });
      }
    } catch (error) {
      console.error("[coveo-cart-sync] init failed", error);
      // A later engine-ready can still recover the element after an early failure.
      if (!this.engine) {
        document.addEventListener(ENGINE_READY_EVENT, this.onEngineReady, { once: true });
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(CART_UPDATE_EVENT, this.syncFromCartJson);
    document.removeEventListener(ENGINE_READY_EVENT, this.onEngineReady);
  }

  // Wire the headless cart actions to an engine and seed the load-time cart. Idempotent: the
  // poll and the ready event can both deliver an engine, so the first one wins; the in-flight
  // guard stops the two racing into a double import/seed. `engine` is assigned only after the
  // actions load so a failed import leaves the element uninitialised and still recoverable.
  async initWithEngine(engine) {
    if (!engine || this.engine || this._initingEngine) return;
    this._initingEngine = true;
    try {
      const { loadCartActions } = await import(COVEO_HEADLESS_CDN);
      this.cartActions = loadCartActions(engine);
      this.engine = engine;
      if (this.latestCart) {
        // The cart changed after load: seed the live cart and announce it, so a cart-context
        // surface that booted this engine re-queries against the cart the shopper now has.
        this.replaceItems(this.latestCart.items.map(mapShopifyLineToCoveoItem));
        this.emitSynced(this.latestCart);
      } else {
        this.seedFromAttribute();
      }
    } finally {
      this._initingEngine = false;
    }
  }

  onEngineReady(event) {
    const engine = (event && event.detail && event.detail.engine) || window.__coveoEngine;
    this.initWithEngine(engine).catch((error) =>
      console.error("[coveo-cart-sync] engine-ready init failed", error),
    );
  }

  // window.__coveoEngine is set by coveo-commerce-init once a surface configures the engine.
  // On a page with a surface it may not be ready at connectedCallback time, so poll briefly.
  resolveEngine() {
    if (window.__coveoEngine) return Promise.resolve(window.__coveoEngine);
    return new Promise((resolve) => {
      let waited = 0;
      const timer = setInterval(() => {
        waited += ENGINE_POLL_STEP_MS;
        if (window.__coveoEngine) {
          clearInterval(timer);
          resolve(window.__coveoEngine);
        } else if (waited >= ENGINE_POLL_LIMIT_MS) {
          clearInterval(timer);
          resolve(null);
        }
      }, ENGINE_POLL_STEP_MS);
    });
  }

  // SEED (1): the host carries the load-time cart as JSON on data-cart.
  seedFromAttribute() {
    let cart;
    try {
      cart = JSON.parse(this.dataset.cart || "{}");
    } catch (error) {
      console.error("[coveo-cart-sync] could not parse data-cart", error);
      return;
    }
    this.replaceItems((cart.items || []).map(mapShopifyLineToCoveoItem));
  }

  // LIVE SYNC (2): re-pull /cart.js and replace the whole Coveo cart on each theme cart change.
  attachLiveSync() {
    document.addEventListener(CART_UPDATE_EVENT, this.syncFromCartJson);
  }

  async syncFromCartJson(event) {
    let dispatched = false;
    let cart = event && event.detail ? event.detail.cart : null;
    if (cart && Array.isArray(cart.items)) this.latestCart = cart;
    try {
      if (this.engine && this.cartActions) {
        /* Use the cart the cart:updated event hands us rather than re-reading /cart.js,
           to avoid racing a concurrent read (e.g. gift-with-purchase). Fall back to the
           fetch when the event carries no line items (a count-only add payload), so the
           Coveo cart still ends up correct, just slower. */
        if (!cart || !Array.isArray(cart.items)) {
          const response = await fetch("/cart.js");
          cart = await response.json();
          this.latestCart = cart;
        }
        dispatched = this.replaceItems((cart.items || []).map(mapShopifyLineToCoveoItem));
      }
    } catch (error) {
      console.error("[coveo-cart-sync] sync from /cart.js failed", error);
    }
    // Emit success ONLY when setItems actually reached the engine, so the synced signal is
    // accurate telemetry and cart-context surfaces re-query against a cart that truly changed.
    // Otherwise emit a skipped signal with its reason so an observer is never left waiting.
    if (dispatched) {
      this.emitSynced(cart);
    } else {
      const reason = this.engine && this.cartActions ? "error" : "no-engine";
      document.dispatchEvent(
        new CustomEvent(CART_SYNC_SKIPPED_EVENT, {
          detail: { reason, itemCount: itemCountOf(cart) },
        }),
      );
    }
  }

  emitSynced(cart) {
    document.dispatchEvent(
      new CustomEvent(CART_SYNCED_EVENT, { detail: { itemCount: itemCountOf(cart) } }),
    );
  }

  // Whole-cart replace via the redux action (the controller has no setItems). Returns whether
  // the dispatch happened so the caller can emit an accurate synced/skipped signal.
  replaceItems(items) {
    if (!this.engine || !this.cartActions) return false;
    this.engine.dispatch(this.cartActions.setItems(items));
    return true;
  }
}

if (!customElements.get("x-coveo-cart-sync")) {
  customElements.define("x-coveo-cart-sync", CoveoCartSync);
}
